import { supabase } from "@/supabase/supabaseClient";
import dummyLogo from "@/assets/logo-query-juriste-png.png";

class PdfGenerator {
  /**
   * Generates the HTML content, uploads it to Supabase Storage, and returns the file path.
   * @param {Object} dossierData - The dossier data object
   * @param {Array} analysesFiscales - Array of analysis points
   * @param {Function} formatTextFn - The text formatting function
   * @returns {Promise<string>} - Promise resolving with the storage file path (not the full URL).
   */
  static async generateAndUploadDossierHTML(
    dossierData,
    analysesFiscales,
    formatTextFn
  ) {
    try {
      // Debug logging

      // Create the full HTML content (including basic structure and styles)
      const htmlContent = await this._createPdfContent(
        dossierData,
        analysesFiscales,
        formatTextFn
      );

      // Define a unique file path for storage (e.g., using dossier ID and timestamp)z
      const dossierId = dossierData.id || "unknown_dossier";
      const timestamp = new Date().toISOString().replace(/[:.-]/g, "_");
      const filePath = `temp_pdfs/${dossierId}_${timestamp}.html`;

      // Upload the HTML content as a file to Supabase Storage
      const { data, error } = await supabase.storage
        .from("dossier-pdfs") // Replace 'dossier-pdfs' with your actual Supabase Storage bucket name
        .upload(filePath, htmlContent, { contentType: "text/html" });

      if (error) {
        console.error("Error uploading HTML to Supabase Storage:", error);
        throw new Error("Failed to upload HTML content to storage.");
      }


      // Return the file path (the backend will construct the public URL)
      return data.path; // Supabase upload returns { path: '...', id: '...' }
    } catch (error) {
      console.error("Error in generateAndUploadDossierHTML:", error);
      throw error;
    }
  }

  /**
   * Generates the HTML content for the PDF without uploading to storage.
   * @param {Object} dossierData - The dossier data object
   * @param {Array} analysesFiscales - Array of analysis points
   * @param {Function} formatTextFn - The text formatting function
   * @returns {string} - The generated HTML content
   */
  static generateDossierHTML(dossierData, analysesFiscales, formatTextFn) {
    return this._createPdfContent(dossierData, analysesFiscales, formatTextFn);
  }

  /**
   * Create the full HTML content for the PDF, including necessary styles.
   * This HTML will be uploaded to storage and fetched by the backend.
   * @private
   */
  static async _createPdfContent(dossierData, analysesFiscales, formatTextFn) {
    const title = dossierData.contexte || "Recherche juridique";
    const currentDate = new Date().toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    // Format the legal framework section
    const synthese = dossierData.synthese
      ? formatTextFn(dossierData.synthese, true)
      : "";

    // Build the analyses section
    // Build the analyses section
    let analysesHTML = "";
    if (analysesFiscales && analysesFiscales.length > 0) {
      analysesFiscales.forEach((pointItem) => {
        const point = pointItem.point_data || pointItem;
        if (!point) {
          return;
        }

        // ⭐ NEW — determine the display label
        const pointLabel = point.point || "Analyse";

        const formattedAnswer = point.final_answer_formatted
          ? formatTextFn(point.final_answer_formatted, true)
          : "";

        // ⭐ NEW — insert an <h3> title before every analysis
        analysesHTML += `
          <div class="pdf-section analysis-point">
            <h3 class="analysis-point-title">${pointLabel}</h3>  <!-- ⭐ -->
            <div class="section-content">
              ${formattedAnswer}
            </div>
          </div>
        `;
      });
    }


    // Return complete HTML document with embedded styles
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Synthèse Juridique</title>
        <meta charset="UTF-8">
        <style>
          @page {
            size: A4;
            margin: 2cm;
          }

          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            margin: 0;
            padding: 0;
            line-height: 1.6;
          }

          .pdf-container {
            padding: 0 10mm !important;
            max-width: 210mm;
            margin: 0 auto;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 15px;
            border-bottom: 2px solid #6c63ff;
            page-break-after: avoid;
            break-inside: avoid;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
          }

          .header img {
            max-width: 100px;
            max-height: 100px;
            height: auto;
            margin-bottom: 8px;
            page-break-after: avoid;
            break-inside: avoid;
          }

          .header h1 {
            font-size: 24px;
            color: #6c63ff;
            margin: 0;
            padding: 0;
            page-break-after: avoid;
            break-inside: avoid;
          }

          .header p {
            font-size: 14px;
            color: #666;
            margin: 0;
            padding: 0;
            page-break-after: avoid;
            break-inside: avoid;
          }

          .pdf-section {
            page-break-inside: avoid;
            break-inside: avoid;
            margin-bottom: 25px;
            orphans: 3;
            widows: 3;
          }

          .section-title {
            font-size: 18px;
            color: #6c63ff;
            padding-bottom: 5px;
            margin-bottom: 15px;
            page-break-after: avoid;
            break-inside: avoid;
          }

          .section-content {
            font-size: 14px;
            text-align: justify;
            line-height: 1.6;
            word-wrap: break-word;
            overflow-wrap: break-word;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .analysis-point {
            margin-bottom: 30px;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .analysis-point h2 {
            font-size: 18px;
            color: #6c63ff;
            margin-bottom: 15px;
            page-break-after: avoid;
            break-inside: avoid;
          }

          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #e0e0e0;
            font-size: 12px;
            color: #888;
            text-align: center;
            page-break-before: avoid;
            break-inside: avoid;
          }

          /* Put this in the <style> block with your other rules */
          .analysis-point-title {
            font-size: 18px;          /* was 16px – slightly larger now */
            font-weight: 600;
            color: #6c63ff;           /* same purple as the rest */
            margin: 0 0 10px 0;
            page-break-after: avoid;
            break-inside: avoid;
          }


          p {
            margin-bottom: 1em;
            orphans: 3;
            widows: 3;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          strong { font-weight: bold; }
          em { font-style: italic; }
          u { text-decoration: underline; }

          ul {
            list-style: none;
            padding: 0;
            margin: 0;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          li {
            margin-bottom: 0.5em;
            text-indent: -1em;
            padding-left: 1em;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          @media print {
            .pdf-section {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }

            h1, h2, h3 {
              page-break-after: avoid !important;
              break-inside: avoid !important;
            }

            p {
              orphans: 3 !important;
              widows: 3 !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }

            .pdf-container {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
          }

          .full-intro {
            page-break-inside: avoid;
            break-inside: avoid;
            margin-bottom: 30px;
          }

          .page-break {
            page-break-after: always;
            break-after: page;
          }
        </style>
      </head>
      <body>
        <div class="pdf-container">
          <div class="pdf-section full-intro">
            <div class="header">
              <img src="${dummyLogo}" alt="Logo" style="max-width: 100px; max-height: 100px; margin-bottom: 8px;">
              <h1>Synthèse Juridique</h1>
              <p>${currentDate}</p>
            </div>

            <p style="font-weight: bold; font-size: 16px; margin-bottom: 20px; text-align: justify;">${title}</p>

            <div>
              <h2 class="section-title">Présentation &amp; Introduction aux résultats de la recherche</h2>
              <div class="section-content">
                ${synthese}
              </div>
            </div>
          </div>

          <div class="page-break"></div>

          <div class="pdf-section">
            <h2 class="section-title">Analyses détaillées</h2>
            <div>
              ${analysesHTML}
            </div>
          </div>

          <div class="footer">
            <p>Document généré par votre agent Query Juriste</p>
            <p>${currentDate}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Sanitize filename to remove invalid characters
   * @private
   */
  static _sanitizeFilename(filename) {
    return filename
      .replace(/[/\\:*?"<>|]/g, "_")
      .replace(/\s+/g, "_")
      .slice(0, 50);
  }
}

export default PdfGenerator;
